import React from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useIntl } from "react-intl";
import cmmsMessages from "../cmmsMessages";
import { formatTimestamp } from "../cmmsFormat";
import { buildScheduleResetEntrySummary } from "../scheduleReset";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "./CmmsStates";

/*
 * When a schedule's counter was put back to zero, newest first.
 *
 * Presentational only, like ToolServiceHistoryPanel next door, and for the same reason rather than
 * out of tidiness: reading the log is Cmms_Field while resetting is Cmms_Manager, so this has to be
 * mountable on a page whose user cannot perform the action it describes.
 *
 * `userNameById` comes from the caller because the user list is one request per page, not one per
 * panel — the same contract the work order and tool history panels take it under.
 */
export default function ScheduleResetHistoryPanel({
  entries,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  isRetrying = false,
  userNameById,
}) {
  const intl = useIntl();

  if (isLoading) {
    return <CmmsListSkeleton rows={3} height={44} />;
  }

  if (isError) {
    return (
      <CmmsErrorState
        error={error}
        fallback={intl.formatMessage(cmmsMessages.resetLogLoadFailed)}
        onRetry={onRetry}
        isRetrying={isRetrying}
      />
    );
  }

  const rows = entries ?? [];

  /*
   * "Never reset" is an answer, not a failure. The hint says when the record starts, because an
   * empty list otherwise reads as a broken feature: the log genuinely begins at the migration
   * that created it, and a counter zeroed before that left nothing behind — which is the whole
   * defect BE-15.19 was written to end.
   */
  if (rows.length === 0) {
    return (
      <CmmsEmptyState
        icon={<RestartAltIcon />}
        title={intl.formatMessage(cmmsMessages.resetLogEmpty)}
        hint={intl.formatMessage(cmmsMessages.resetLogEmptyHint)}
      />
    );
  }

  return (
    <Stack divider={<Divider flexItem />} spacing={0}>
      {rows.map((entry) => (
        // Keyed on id and source together: the two lanes store their entries in different tables,
        // so a tool entry and an asset entry can share an id. They never appear in one list today
        // — a schedule is on one lane — but a key that is unique only by accident is a key that
        // breaks the day the lists are merged.
        <Box key={`${entry.source}-${entry.id}`} sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatTimestamp(intl, entry.resetAt)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
            {buildScheduleResetEntrySummary(entry, intl, userNameById?.get(entry.resetByUserId))}
          </Typography>
          {entry.notes &&
            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {entry.notes}
            </Typography>
          }
        </Box>
      ))}
    </Stack>
  );
}
